import { FC } from 'react';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';

// MUI
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// screen
import type { NutritionMenuFilterModel } from 'modules/map/components/nutrition-menu/types';
import { formatUTC } from 'modules/map/components/pdf-print-form/utils';
import { TeacherMenuModel } from 'modules/map/components/teacher-menu/types';

interface Props {
  data: TeacherMenuModel;
  filter: NutritionMenuFilterModel;
}
const TeacherMenuTable: FC<Props> = ({ data, filter }) => {
  const intl = useIntl();

  const days = data.structuredData?.data?.items || [];

  const fromDate = formatUTC(days[0]?.datetime, 'L');
  const toDate = formatUTC(days[days.length - 1]?.datetime, 'L');

  const isNutritionMenu = data.structuredData?.metadata.isNutritionMenu;

  return (
    <TableContainer
      className="px-2 tablet:px-4"
      sx={{
        mt: 2,
        '& td,th': {
          border: '0.5px solid',
          textAlign: 'center',
          padding: 1,
        },
        '@media print': {
          '& table': {
            borderCollapse: 'collapse',
          },
          '& td,th': {
            background: 'none',
            color: 'black',
          },
          '& thead': {
            background: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'none',
          '@media print': {
            display: 'flex',
            justifyContent: 'space-around',
            color: 'black',
            marginBottom: 2,
          },
        }}
      >
        <Typography variant="body1">{dayjs().format('LL')}</Typography>
        <Typography variant="h6" textAlign={'center'} flex={1}>
          <FormattedMessage id="map.nutritionMenu" /> &nbsp;(
          {[fromDate, toDate].join(' - ')})
        </Typography>
        <Typography variant="h6">
          <FormattedMessage id="map.process" />
          :&nbsp;
          {intl.formatMessage({ id: 'map.' + filter.state })}
        </Typography>
      </Box>
      <Table sx={{ borderCollapse: 'separate' }}>
        <TableHead
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            background: theme.palette.background.paper,
          })}
        >
          <TableRow>
            {(isNutritionMenu
              ? [
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.day" />,
                  },
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.menu" />,
                  },
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.note" />,
                  },
                ]
              : [
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.day" />,
                  },
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.menu" />,
                  },
                  {
                    rowSpan: 2,
                    label: <FormattedMessage id="map.note" />,
                  },
                ]
            ).map((item, index) => {
              return (
                <TableCell key={index} rowSpan={item.rowSpan} className="whitespace-pre-wrap">
                  {item.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day, idx) => {
            const shouldBreakNotes = day.note.includes('\n');
            const noteOfTheDay = shouldBreakNotes
              ? day.note.split('\n').map((note, index) => {
                  return (
                    <Typography key={index} sx={{ my: 1 }}>
                      {note}
                    </Typography>
                  );
                })
              : day.note;

            return (
              <Fragment key={idx}>
                {day.dishes.map((dish, index) => {
                  const isFistRow = index === 0;

                  return (
                    <TableRow key={index}>
                      {isFistRow && (
                        <TableCell rowSpan={day.dishes.length}>
                          <span className="capitalize">{formatUTC(day.datetime, 'dddd')}</span>
                          <br />
                          {day.datetime && formatUTC(day.datetime, 'L')}
                        </TableCell>
                      )}
                      <TableCell>{dish}</TableCell>
                      {isFistRow && (
                        <TableCell rowSpan={day.dishes.length}>{noteOfTheDay}</TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TeacherMenuTable;
